import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, CircularProgress, Stack,
  Alert, Card, CardContent, Chip, Divider, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, useTheme, IconButton, Tooltip
} from '@mui/material';
import {
  AutoFixHigh, Send, Warning, CheckCircle, Lock,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux.hooks';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';

interface AccountMatch {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  score: number;
  matchedTokens: string[];
}
interface EmployeeMatch {
  employeeId: number;
  employeeCode: string;
  fullName: string;
  fullNameAr: string;
  score: number;
}
interface CostCenterMatch {
  costCenterId: number;
  costCenterCode: string;
  costCenterName: string;
  costCenterNameAr: string;
}
interface ParsedLine {
  lineNumber: number;
  originalText: string;
  amount: number;
  debitAccount: AccountMatch | null;
  accountAlternatives: AccountMatch[];
  description: string;
  requiresCostCenter: boolean;
  costCenter: CostCenterMatch | null;
  costCenterAlternatives: CostCenterMatch[];
  warnings: string[];
}
interface ParseResponse {
  success: boolean;
  originalText: string;
  normalizedText: string;
  tokens: string[];
  outputLanguage: 'en' | 'ar';
  voucherType: string;
  previewVoucherNumber: string;
  date: string;
  payeeName: string | null;
  payeeEmployee: EmployeeMatch | null;
  payeeAlternatives: EmployeeMatch[];
  creditAccount: AccountMatch | null;
  lines: ParsedLine[];
  totalAmount: number;
  warnings: string[];
  notes: string[];
  isPreviewOnly: boolean;
}

const SAMPLES = [
  'مخصص اجازات المنقف 184.600 Sayed Abdul Sattar 1-2-2026 V 6283',
  'عهدة سعد 250 لعبدالمعين',
  'صرف 25.9 كمبيوتر هارد ديسك ، 33.5 قرطاسية مكتب ، 32.6 ضيافة مطبخ',
  'camp maintenance 90 robin',
  'bakery leave mahmoud zayed 196 - en',
  'عهدة مارينا 100 - ar',
];

const SmartEntryDemoPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isSuperAdmin = useAppSelector(s => s.auth.user?.isSuperAdmin ?? false);

  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ParseResponse | null>(null);

  // Super Admin gate
  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleParse = async () => {
    if (!text.trim()) {
      toast.warning(isRTL ? 'اكتب نص السند أولاً' : 'Enter voucher text first');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axiosInstance.post<ParseResponse>('/SmartEntry/parse', { text });
      setResult(data);
      if (data.success) {
        toast.success(isRTL ? 'تم التحليل' : 'Parsed');
      } else {
        toast.warning(isRTL ? 'تحليل جزئي — راجع التحذيرات' : 'Partial parse — check warnings');
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || (e?.response?.status === 403
        ? (isRTL ? 'مرفوض — للسوبر أدمن فقط' : 'Forbidden — Super Admin only')
        : (isRTL ? 'فشل التحليل' : 'Parse failed'));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n: number) => (n || 0).toFixed(3);
  const accentBg = isDark ? '#0f172a' : '#fafafa';
  const cardBg = isDark ? '#1e293b' : 'white';
  const headerBg = isDark ? '#1a237e' : '#e3f2fd';
  const headerText = isDark ? '#90caf9' : '#0d47a1';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, direction: isRTL ? 'rtl' : 'ltr', bgcolor: accentBg, minHeight: '100vh', maxWidth: 1600, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <AutoFixHigh sx={{ fontSize: 40, color: '#7c4dff' }} />
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{
            background: 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            {isRTL ? 'الإدخال الذكي للسندات (تجريبي)' : 'Smart Voucher Entry (Demo)'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
            <Chip icon={<Lock fontSize="small" />} size="small" label={isRTL ? 'السوبر أدمن فقط' : 'Super Admin Only'} color="warning" />
            <Chip size="small" label={isRTL ? 'بدون حفظ في DB' : 'No DB writes'} color="info" />
          </Stack>
        </Box>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        {isRTL
          ? 'اكتب نص سند الصرف النقدي بأي صيغة (عربي/إنجليزي/مختلط). النظام يحلل ويعرض السند المقترح كاملاً بدون أي حفظ. الحساب الدائن دائماً = الصندوق الرئيسي.'
          : 'Type a cash payment voucher in any format (Arabic/English/mixed). The system parses and previews a complete voucher with NO DB writes. The credit side is always the main cash box.'}
        <Box mt={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {isRTL
              ? '💡 لغة المخرجات: ضع " - en" في نهاية النص للإنجليزي (افتراضي)، أو " - ar" للعربي. حتى لو كتبت بالعربي، المخرجات ستكون بالإنجليزي افتراضياً.'
              : '💡 Output language: append " - en" for English (default) or " - ar" for Arabic. Even Arabic input produces English output by default.'}
          </Typography>
        </Box>
      </Alert>

      {/* Input area */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: cardBg }}>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={isRTL
            ? 'مثال: عهدة سعد 250 لعبدالمعين  أو  صرف 25.9 كمبيوتر ، 33.5 قرطاسية'
            : 'e.g. petty saad 250 abdul moaeen  or  25.9 computer hard disk , 33.5 stationery'}
          variant="outlined"
          sx={{ mb: 2 }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Send />}
            onClick={handleParse}
            disabled={loading}
            sx={{ background: 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)' }}
          >
            {isRTL ? 'تحليل النص' : 'Parse'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {isRTL ? 'أمثلة جاهزة:' : 'Samples:'}
          </Typography>
          {SAMPLES.map((s, i) => (
            <Chip
              key={i}
              size="small"
              label={s}
              onClick={() => setText(s)}
              sx={{ cursor: 'pointer', mb: 0.5 }}
            />
          ))}
        </Stack>
      </Paper>

      {/* Result preview — voucher-style layout. outLang drives whether En or Ar is "primary" */}
      {result && (() => {
        const outLang = result.outputLanguage || 'en';
        // Shadow the outer isRTL so the entire result block follows the chosen output language
        // (not the page UI language). This way "- en" gives English UI even on an Arabic page.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const isRTL = outLang === 'ar';
        const accName = (a: AccountMatch | null) => a ? (isRTL ? (a.accountNameAr || a.accountName) : (a.accountName || a.accountNameAr)) : '';
        const accNameAlt = (a: AccountMatch | null) => a ? (isRTL ? (a.accountName || '') : (a.accountNameAr || '')) : '';
        const empName = (e: EmployeeMatch | null) => e ? (isRTL ? (e.fullNameAr || e.fullName) : (e.fullName || e.fullNameAr)) : '';
        const ccName = (cc: CostCenterMatch | null) => cc ? (isRTL ? (cc.costCenterNameAr || cc.costCenterName) : (cc.costCenterName || cc.costCenterNameAr)) : '';
        return (
        <Box>
          {/* Top banner */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h5" fontWeight="bold">
              {result.success
                ? <CheckCircle sx={{ verticalAlign: 'middle', mr: 1, color: 'success.main' }} />
                : <Warning sx={{ verticalAlign: 'middle', mr: 1, color: 'warning.main' }} />}
              {isRTL ? 'معاينة سند الصرف النقدي' : 'Cash Payment Voucher Preview'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={outLang === 'ar' ? '🌐 لغة المخرجات: العربية' : '🌐 Output: English'}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
              <Chip label={isRTL ? '⚠️ تجريبي — لن يُحفظ' : '⚠️ Preview only — NOT saved'} color="warning" sx={{ fontSize: '0.9rem', py: 2 }} />
            </Stack>
          </Stack>

          {result.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Stack spacing={0.5}>
                {result.warnings.map((w, i) => <Typography key={i} variant="body2">{w}</Typography>)}
              </Stack>
            </Alert>
          )}

          {result.notes.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Stack spacing={0.5}>
                {result.notes.map((n, i) => <Typography key={i} variant="body2">📝 {n}</Typography>)}
              </Stack>
            </Alert>
          )}

          {/* Header — voucher info card (full width, big text) */}
          <Paper sx={{ p: 3, mb: 2, bgcolor: cardBg, borderRadius: 2, border: 2, borderColor: isDark ? '#3949ab' : '#1976d2' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>{isRTL ? 'نوع السند' : 'Type'}</Typography>
                <Typography variant="h6" sx={{ color: headerText, fontWeight: 'bold' }}>
                  {result.voucherType === 'CP' ? (isRTL ? 'صرف نقدي' : 'Cash Payment') : (isRTL ? 'صرف بشيك' : 'Cheque Payment')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>{isRTL ? 'رقم السند (تقديري)' : 'Voucher Number (preview)'}</Typography>
                <Typography variant="h6" sx={{ color: headerText, fontFamily: 'monospace' }}>{result.previewVoucherNumber}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>{isRTL ? 'التاريخ' : 'Date'}</Typography>
                <Typography variant="h6" sx={{ color: headerText }}>{result.date?.substring(0, 10)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>{isRTL ? 'الإجمالي' : 'Total'}</Typography>
                <Typography variant="h5" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>{fmt(result.totalAmount)} KD</Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>{isRTL ? 'المستفيد' : 'Payee'}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.05rem' }}>
                  {result.payeeEmployee
                    ? `${empName(result.payeeEmployee)} (${result.payeeEmployee.employeeCode})`
                    : (result.payeeName || (isRTL ? '— غير محدد —' : '— not detected —'))}
                </Typography>
                {result.payeeAlternatives.length > 0 && (
                  <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" alignItems="center">
                    <Typography variant="caption" color="text.secondary">{isRTL ? 'بدائل:' : 'Alts:'}</Typography>
                    {result.payeeAlternatives.map(a => (
                      <Chip key={a.employeeId} size="small" variant="outlined" label={empName(a)} />
                    ))}
                  </Stack>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>{isRTL ? 'الطرف الدائن (تلقائي)' : 'Credit Account (auto)'}</Typography>
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Chip size="small" label={result.creditAccount?.accountCode || '?'} sx={{ fontFamily: 'monospace', bgcolor: '#1565c0', color: 'white' }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {result.creditAccount ? accName(result.creditAccount) : '—'}
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Paper>

          {/* Lines as cards (one per line, full width) */}
          <Typography variant="h6" fontWeight="bold" mb={1.5}>
            {isRTL ? `سطور المدين (${result.lines.length})` : `Debit Lines (${result.lines.length})`}
          </Typography>

          {result.lines.length === 0 && (
            <Alert severity="error">{isRTL ? 'لم يتم استخراج أي سطر' : 'No lines extracted'}</Alert>
          )}

          {result.lines.map(line => (
            <Paper key={line.lineNumber} sx={{
              p: 2.5, mb: 2, bgcolor: cardBg, borderRadius: 2,
              borderLeft: 6, borderColor: line.debitAccount
                ? (line.debitAccount.score >= 6 ? '#2e7d32' : line.debitAccount.score >= 3 ? '#f57c00' : '#9e9e9e')
                : '#d32f2f'
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Chip label={`#${line.lineNumber}`} sx={{ fontWeight: 'bold', bgcolor: isDark ? '#37474f' : '#eceff1' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    "{line.originalText}"
                  </Typography>
                </Stack>
                {line.debitAccount && (
                  <Chip
                    label={
                      line.debitAccount.score >= 6
                        ? (isRTL ? '✓ ثقة عالية' : '✓ High confidence')
                        : line.debitAccount.score >= 3
                          ? (isRTL ? '⚠ ثقة متوسطة' : '⚠ Medium confidence')
                          : (isRTL ? '⚠ ثقة منخفضة — راجع البدائل' : '⚠ Low confidence — review alts')
                    }
                    color={line.debitAccount.score >= 6 ? 'success' : line.debitAccount.score >= 3 ? 'warning' : 'default'}
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Stack>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 2fr 1.5fr' }, gap: 2, alignItems: 'start' }}>
                {/* Debit account */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>{isRTL ? 'الحساب المدين' : 'Debit Account'}</Typography>
                  {line.debitAccount ? (
                    <>
                      <Stack direction="row" spacing={1} alignItems="baseline" mt={0.5}>
                        <Chip size="small" label={line.debitAccount.accountCode} sx={{ fontFamily: 'monospace', bgcolor: '#1565c0', color: 'white' }} />
                      </Stack>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                        {accName(line.debitAccount)}
                      </Typography>
                      {accNameAlt(line.debitAccount) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{accNameAlt(line.debitAccount)}</Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>{isRTL ? '— لم يُعثر على حساب —' : '— no account found —'}</Typography>
                  )}
                </Box>

                {/* Amount */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>{isRTL ? 'المبلغ' : 'Amount'}</Typography>
                  <Typography variant="h5" sx={{ color: '#1565c0', fontWeight: 'bold', mt: 0.5 }}>{fmt(line.amount)}</Typography>
                  <Typography variant="caption" color="text.secondary">KD</Typography>
                </Box>

                {/* Description */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>{isRTL ? 'الوصف' : 'Description'}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>{line.description}</Typography>
                </Box>

                {/* Cost Center */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>{isRTL ? 'مركز التكلفة' : 'Cost Center'}</Typography>
                  {line.requiresCostCenter ? (
                    line.costCenter ? (
                      <Box mt={0.5}>
                        <Chip size="small" label={line.costCenter.costCenterCode} color="success" sx={{ fontFamily: 'monospace' }} />
                        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                          {ccName(line.costCenter)}
                        </Typography>
                      </Box>
                    ) : (
                      <Chip size="small" label={isRTL ? '🔴 مطلوب — لم يُحدَّد' : '🔴 Required — missing'} color="error" sx={{ mt: 0.5 }} />
                    )
                  ) : (
                    <Chip size="small" label={isRTL ? 'غير مطلوب' : 'N/A'} variant="outlined" sx={{ mt: 0.5 }} />
                  )}
                </Box>
              </Box>

              {/* Alternatives */}
              {line.accountAlternatives.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{isRTL ? 'حسابات بديلة محتملة:' : 'Alternative accounts:'}</Typography>
                  <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
                    {line.accountAlternatives.slice(0, 3).map(a => (
                      <Chip
                        key={a.accountId}
                        size="small"
                        variant="outlined"
                        label={`${a.accountCode} — ${accName(a)}`}
                        sx={{ maxWidth: 'none' }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {line.warnings.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                  {line.warnings.map((w, i) => <Typography key={i} variant="caption" sx={{ display: 'block' }}>{w}</Typography>)}
                </Alert>
              )}
            </Paper>
          ))}

          {/* Diagnostics — collapsible */}
          <Paper sx={{ p: 2, mt: 3, bgcolor: cardBg, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1} color="text.secondary">
              🔍 {isRTL ? 'تفاصيل التشخيص (للمراجعة)' : 'Diagnostics (for review)'}
            </Typography>
            <Box>
              <Typography variant="caption" color="text.secondary">{isRTL ? 'النص المُطَبَّع:' : 'Normalized:'}</Typography>
              <Typography variant="body2" sx={{
                fontFamily: 'monospace', bgcolor: accentBg, p: 1, borderRadius: 1,
                mt: 0.5, wordBreak: 'break-word', fontSize: '0.85rem'
              }}>{result.normalizedText}</Typography>
            </Box>
            <Box mt={1.5}>
              <Typography variant="caption" color="text.secondary">{isRTL ? `الكلمات المُستخرَجة (${result.tokens.length}):` : `Tokens (${result.tokens.length}):`}</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.5}>
                {result.tokens.map((t, i) => <Chip key={i} size="small" label={t} variant="outlined" />)}
              </Stack>
            </Box>
          </Paper>

          <Alert severity="info" sx={{ mt: 2 }}>
            {isRTL
              ? 'هذه معاينة فقط. لم يُحفظ أي شيء في قاعدة البيانات. زر "Save" غير موجود في النسخة التجريبية.'
              : 'Preview only. Nothing was saved to the database. There is no "Save" button in this demo.'}
          </Alert>
        </Box>
        );
      })()}
    </Box>
  );
};

export default SmartEntryDemoPage;
